import { infiniteQueryOptions, useInfiniteQuery } from '@tanstack/react-query';
import flatMap from 'lodash/flatMap';

import { SortDirection } from '@cast/design-system';
import { WorkloadGpuUtilization } from '@cast/types';

import { apiClient } from '@cast/infrastructure-api';

const WORKLOADS_GPU_UTILIZATION_QUERY_KEY = 'workloads-gpu-utilization';

type UseWorkloadsGpuUtilizationQueryProps = {
  clusterIds: string[];
  pageLimit?: number;
  sortBy?: string;
  sortDirection?: SortDirection;
  search?: string;
};

type UseWorkloadsGpuUtilizationQuery = {
  data?: WorkloadGpuUtilization[];
  isLoading: boolean;
  error?: unknown;
  refetch?: () => void;
  hasNextPage: boolean;
  fetchNextPage: () => void;
  isFetchingNextPage: boolean;
  isFetching: boolean;
  count: number;
};

const getWorkloadsGpuUtilizationQueryKey = ({
  clusterIds,
  pageLimit,
  search,
  sortBy,
  sortDirection,
}: Pick<
  UseWorkloadsGpuUtilizationQueryProps,
  'clusterIds' | 'sortBy' | 'sortDirection' | 'search'
> & { pageLimit: number }) =>
  [
    WORKLOADS_GPU_UTILIZATION_QUERY_KEY,
    clusterIds,
    pageLimit,
    search,
    sortBy,
    sortDirection,
  ] as const;

const getWorkloadsGpuUtilizationQueryOptions = ({
  clusterIds,
  sortBy,
  sortDirection,
  search,
  pageLimit = 50,
}: UseWorkloadsGpuUtilizationQueryProps) =>
  infiniteQueryOptions({
    queryKey: getWorkloadsGpuUtilizationQueryKey({
      clusterIds,
      pageLimit,
      search,
      sortBy,
      sortDirection,
    }),
    queryFn: async ({ pageParam }) => {
      const { data } = await apiClient.costReport.getWorkloadsGpuUtilization({
        filter: {
          clusterIds,
          search,
        },
        pageLimit: String(pageLimit),
        pageCursor: pageParam,
        sortField: sortBy,
        sortOrder: sortDirection,
      });

      return data;
    },
    refetchOnWindowFocus: false,
    initialPageParam: '',
    getNextPageParam: (lastPage) => {
      return lastPage.nextCursor || undefined;
    },
  });

export const useWorkloadsGpuUtilizationQuery = (
  props: UseWorkloadsGpuUtilizationQueryProps
): UseWorkloadsGpuUtilizationQuery => {
  const {
    data,
    isLoading,
    error,
    refetch,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
    isFetching,
  } = useInfiniteQuery(getWorkloadsGpuUtilizationQueryOptions(props));

  return {
    isLoading,
    data: flatMap(data?.pages, (page) => page?.workloads ?? []),
    error,
    refetch,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isFetching,
    count: Number(data?.pages[0].count ?? 0),
  };
};
